import {z} from 'zod';
import {defineToolRegistryManifest} from '../../types/base-tool-types';
import {dataToolSchemas} from '../../utils/schemas';
import {CMS_RICH_TEXT_WHTML_GRAMMAR} from '../cms-rich-text-whtml-grammar';

const toolName = 'update-cms-collection-items' as const;

const itemUpdateZod = z.object({
  id: dataToolSchemas.itemId,
  cmsLocaleId: dataToolSchemas.localeId.optional(),
  isArchived: z
    .boolean()
    .describe('Whether the item should be archived')
    .optional(),
  isDraft: z
    .boolean()
    .describe('Whether the item should be a draft')
    .optional(),
  fieldData: z
    .object({
      name: z
        .string()
        .min(1)
        .optional()
        .describe('The name of the collection item'),
      slug: z
        .string()
        .min(1)
        .optional()
        .describe('The URL slug for the collection item'),
    })
    .passthrough()
    .describe(
      'The field data to update for this collection item. Can include name, slug, and any custom fields. Field names should be the field slugs (which are always in kebab-case), not the free-form field names.'
    ),
});

const argumentZod = z.object({
  collectionId: dataToolSchemas.collectionId,
  items: z
    .array(itemUpdateZod)
    .min(1)
    .max(100)
    .describe('Array of items to update (max 100 items)'),
  skipInvalidFiles: z
    .boolean()
    .optional()
    .describe(
      'Whether to skip invalid files during processing. Defaults to true.'
    ),
});

export const updateCMSCollectionItemsManifest = defineToolRegistryManifest({
  id: toolName,
  prettyName: 'Update CMS collection items',
  family: 'cms',
  llmTool: {
    name: toolName,
    description:
      'Update multiple items in a CMS collection in a single operation. This tool allows you to efficiently update up to 100 collection items at once with different field data. All updates will be applied to staging by default. Important note: the items details should be fetched first, in order to get the most recent data.\n\n' +
      CMS_RICH_TEXT_WHTML_GRAMMAR,
    inputSchema: argumentZod,
  },
  type: 'server',
  permission: [
    {
      resource: 'SiteCollection',
      action: 'manage',
      resolveResources: (args) => [
        {type: 'SiteCollection' as const, id: args.collectionId},
      ],
    },
  ],
  confirmBeforeExecuting: true,
});

export type UpdateCMSCollectionItemsArgumentType = z.infer<typeof argumentZod>;
